import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Plus,
  Search,
  Loader2,
  X,
  MapPin,
  Briefcase,
  ToggleLeft,
  ToggleRight,
  Building2,
  HardDriveUpload,
  RefreshCw,
  Link2,
  Link2Off,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  AjouterLieuDialog,
  CampagneFormDialog,
  CampagneDetailDrawer,
  STATUTS,
  FILIERES,
  type LieuRecrutement,
  type CampagneRow,
} from "@/pages/rh/campagnes";

// ─── Onglet Bureaux ────────────────────────────────────────────────────────────

function BureauxTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const { data: lieux = [], isLoading } = useQuery<LieuRecrutement[]>({
    queryKey: ["/api/lieux-recrutement"],
    queryFn: async () => (await authFetch("/api/lieux-recrutement")).json(),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, actif }: { id: string; actif: boolean }) => {
      const r = await authFetch(`/api/lieux-recrutement/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ actif }),
      });
      if (!r.ok) throw new Error((await r.json()).message || "Erreur");
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/lieux-recrutement"] }),
    onError: (e: Error) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{lieux.length} bureau(x)</p>
        <Button size="sm" onClick={() => setAddOpen(true)} data-testid="btn-ajouter-bureau-params">
          <Plus className="w-4 h-4 mr-1" /> Ajouter un bureau
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-24">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : lieux.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          <Building2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
          Aucun bureau enregistré
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Libellé</TableHead>
                <TableHead>Ville</TableHead>
                <TableHead>CP</TableHead>
                <TableHead className="text-center">Statut</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {lieux.map((l) => (
                <TableRow key={l.id} data-testid={`row-lieu-${l.id}`}>
                  <TableCell className="font-medium">{l.libelle}</TableCell>
                  <TableCell className="flex items-center gap-1 text-muted-foreground">
                    <MapPin className="w-3 h-3 shrink-0" />{l.ville}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{l.codePostal || "—"}</TableCell>
                  <TableCell className="text-center">
                    <Badge className={l.actif
                      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                      : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                    }>
                      {l.actif ? "Actif" : "Inactif"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      title={l.actif ? "Désactiver" : "Activer"}
                      disabled={toggleMutation.isPending}
                      onClick={() => toggleMutation.mutate({ id: l.id, actif: !l.actif })}
                      data-testid={`btn-toggle-lieu-${l.id}`}
                    >
                      {l.actif
                        ? <ToggleRight className="w-5 h-5 text-green-600" />
                        : <ToggleLeft className="w-5 h-5 text-muted-foreground" />
                      }
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AjouterLieuDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ["/api/lieux-recrutement"] });
          setAddOpen(false);
        }}
      />
    </div>
  );
}

// ─── Onglet Campagnes ──────────────────────────────────────────────────────────

function CampagnesTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filtStatut, setFiltStatut] = useState("all");
  const [filtFiliere, setFiltFiliere] = useState("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editCampagne, setEditCampagne] = useState<CampagneRow | null>(null);

  const { data: campagnes = [], isLoading } = useQuery<CampagneRow[]>({
    queryKey: ["/api/campagnes"],
    queryFn: async () => (await authFetch("/api/campagnes")).json(),
  });

  const { data: lieux = [] } = useQuery<LieuRecrutement[]>({
    queryKey: ["/api/lieux-recrutement"],
    queryFn: async () => (await authFetch("/api/lieux-recrutement")).json(),
  });

  const filtered = campagnes.filter((c) => {
    const matchSearch = !search ||
      c.intitule.toLowerCase().includes(search.toLowerCase()) ||
      (c.lieu_ville ?? "").toLowerCase().includes(search.toLowerCase());
    const matchStatut = filtStatut === "all" || c.statut === filtStatut;
    const matchFiliere = filtFiliere === "all" || c.filiere === filtFiliere;
    return matchSearch && matchStatut && matchFiliere;
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">{campagnes.length} campagne(s)</p>
        <Button size="sm" onClick={() => { setEditCampagne(null); setFormOpen(true); }} data-testid="btn-nouvelle-campagne-params">
          <Plus className="w-4 h-4 mr-1" /> Créer une campagne
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 w-44"
            data-testid="input-search-campagnes-params"
          />
        </div>
        <Select value={filtStatut} onValueChange={setFiltStatut}>
          <SelectTrigger className="h-8 w-36" data-testid="select-filter-statut-camp-params">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {STATUTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtFiliere} onValueChange={setFiltFiliere}>
          <SelectTrigger className="h-8 w-36" data-testid="select-filter-filiere-camp-params">
            <SelectValue placeholder="Filière" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes filières</SelectItem>
            {FILIERES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {(filtStatut !== "all" || filtFiliere !== "all" || search) && (
          <Button size="sm" variant="ghost" className="h-8"
            onClick={() => { setSearch(""); setFiltStatut("all"); setFiltFiliere("all"); }}
            data-testid="btn-reset-filters-camp-params"
          >
            <X className="w-4 h-4 mr-1" /> Réinitialiser
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-24">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          <Briefcase className="w-8 h-8 mx-auto mb-2 opacity-30" />
          Aucune campagne trouvée
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Intitulé</TableHead>
                <TableHead>Bureau</TableHead>
                <TableHead>Filière</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-center">Candidats</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => {
                const statutInfo = STATUTS.find((s) => s.value === c.statut);
                return (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() => setSelectedId(c.id)}
                    data-testid={`row-campagne-params-${c.id}`}
                  >
                    <TableCell className="font-medium">{c.intitule}</TableCell>
                    <TableCell>
                      {c.lieu_ville
                        ? <span className="flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="w-3 h-3" />{c.lieu_ville}</span>
                        : <span className="text-muted-foreground text-xs">—</span>}
                    </TableCell>
                    <TableCell>
                      {c.filiere
                        ? <Badge variant="outline" className="text-xs capitalize">{c.filiere}</Badge>
                        : <span className="text-muted-foreground text-xs">—</span>}
                    </TableCell>
                    <TableCell>
                      {statutInfo && <Badge className={`text-xs ${statutInfo.color}`}>{statutInfo.label}</Badge>}
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">{c.nb_candidats}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <CampagneFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditCampagne(null); }}
        lieux={lieux}
        onRefreshLieux={() => qc.invalidateQueries({ queryKey: ["/api/lieux-recrutement"] })}
        campagne={editCampagne}
      />
      <CampagneDetailDrawer
        campagneId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

// ─── Onglet Drive ──────────────────────────────────────────────────────────────

interface DriveDossierItem {
  driveFolderId: string;
  driveFolderName: string;
  parsed: { poste: string; contrat: string | null; filiere: string | null } | null;
  mapping: {
    id: string;
    campagne_id: string | null;
    campagne_intitule: string | null;
    campagne_filiere: string | null;
    actif: boolean;
    last_sync_at: string | null;
    last_sync_recap: { nouveaux: number; deja_ingeres: number; erreurs: number } | null;
  } | null;
}

function MapperDialog({
  dossier,
  campagnes,
  onClose,
  onMapped,
}: {
  dossier: DriveDossierItem;
  campagnes: Array<{ id: string; intitule: string }>;
  onClose: () => void;
  onMapped: () => void;
}) {
  const { toast } = useToast();
  const [mode, setMode] = useState<"existing" | "create">("existing");
  const [campagneId, setCampagneId] = useState("");
  const [intitule, setIntitule] = useState(dossier.parsed?.poste ?? dossier.driveFolderName);
  const [filiere, setFiliere] = useState(dossier.parsed?.filiere ?? "");
  const [contrat, setContrat] = useState(dossier.parsed?.contrat ?? "");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (mode === "existing" && !campagneId) {
      toast({ title: "Choisissez une campagne", variant: "destructive" }); return;
    }
    if (mode === "create" && !intitule.trim()) {
      toast({ title: "Intitulé obligatoire", variant: "destructive" }); return;
    }
    setLoading(true);
    try {
      const body: any = {
        drive_folder_id: dossier.driveFolderId,
        drive_folder_name: dossier.driveFolderName,
      };
      if (mode === "existing") {
        body.campagne_id = campagneId;
      } else {
        body.create_campagne = {
          intitule: intitule.trim(),
          filiere: filiere || null,
          contrat: contrat || null,
        };
      }
      const r = await authFetch("/api/rh/drive/dossiers/mapper", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      onMapped();
      onClose();
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm font-medium">Mapper « {dossier.driveFolderName} »</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button size="sm" variant={mode === "existing" ? "default" : "outline"} onClick={() => setMode("existing")} className="flex-1 text-xs">
              Lier à une campagne existante
            </Button>
            <Button size="sm" variant={mode === "create" ? "default" : "outline"} onClick={() => setMode("create")} className="flex-1 text-xs">
              Créer une campagne
            </Button>
          </div>

          {mode === "existing" ? (
            <div>
              <Label className="text-xs">Campagne</Label>
              <Select value={campagneId || "none"} onValueChange={(v) => setCampagneId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="select-mapper-campagne"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {campagnes.map((c) => <SelectItem key={c.id} value={c.id}>{c.intitule}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Intitulé *</Label>
                <Input value={intitule} onChange={(e) => setIntitule(e.target.value)} placeholder="Technicien terrain H/F" data-testid="input-mapper-intitule" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Filière</Label>
                  <Select value={filiere || "none"} onValueChange={(v) => setFiliere(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="select-mapper-filiere"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      <SelectItem value="technique">Technique</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="administratif">Administratif</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Contrat</Label>
                  <Select value={contrat || "none"} onValueChange={(v) => setContrat(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="select-mapper-contrat"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      <SelectItem value="cdi">CDI</SelectItem>
                      <SelectItem value="cdd">CDD</SelectItem>
                      <SelectItem value="alternance">Alternance</SelectItem>
                      <SelectItem value="stage">Stage</SelectItem>
                      <SelectItem value="freelance">Freelance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={loading} data-testid="btn-confirmer-mapping">
            {loading && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Mapper
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DriveTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [syncing, setSyncing] = useState(false);
  const [syncRecap, setSyncRecap] = useState<{ dossiers: Array<{ folderName: string; nouveaux: number; deja_ingeres: number; erreurs: number }> } | null>(null);
  const [mapperDossier, setMapperDossier] = useState<DriveDossierItem | null>(null);

  const { data, isLoading, refetch } = useQuery<{ dossiers: DriveDossierItem[]; driveError: string | null }>({
    queryKey: ["/api/rh/drive/dossiers"],
    queryFn: async () => (await authFetch("/api/rh/drive/dossiers")).json(),
  });

  const { data: campagnes = [] } = useQuery<Array<{ id: string; intitule: string }>>({
    queryKey: ["/api/campagnes"],
    queryFn: async () => (await authFetch("/api/campagnes")).json(),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, actif }: { id: string; actif: boolean }) => {
      const r = await authFetch(`/api/rh/drive/dossiers/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ actif }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      return r.json();
    },
    onSuccess: () => refetch(),
    onError: (e: Error) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const synchroniser = async () => {
    setSyncing(true);
    try {
      const r = await authFetch("/api/rh/drive/synchroniser", { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || "Erreur synchronisation");
      setSyncRecap(d);
      refetch();
      qc.invalidateQueries({ queryKey: ["/api/candidats"] });
      toast({ title: "Synchronisation terminée" });
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setSyncing(false);
    }
  };

  const dossiers = data?.dossiers ?? [];
  const driveError = data?.driveError ?? null;

  return (
    <div className="space-y-4">
      {/* Bandeau erreur Drive */}
      {driveError && (
        <div className="flex items-start gap-2 rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Dossier Drive inaccessible</p>
            <p className="text-xs mt-0.5 opacity-80">Vérifiez que le dossier 21_CVthèque est partagé avec le compte de service, et que les secrets GOOGLE_SA_KEY et DRIVE_CVTHEQUE_FOLDER_ID sont configurés.</p>
            <p className="text-xs mt-1 font-mono opacity-60">{driveError}</p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{dossiers.length} dossier(s) détecté(s)</p>
        <Button size="sm" onClick={synchroniser} disabled={syncing} data-testid="btn-drive-synchroniser">
          {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Synchroniser maintenant
        </Button>
      </div>

      {/* Récap dernière synchro */}
      {syncRecap && syncRecap.dossiers.length > 0 && (
        <div className="rounded-md bg-muted/50 border p-3 space-y-1">
          <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> Résultat de la dernière synchronisation
          </p>
          {syncRecap.dossiers.map((d, i) => (
            <p key={i} className="text-xs text-muted-foreground">
              <span className="font-medium">{d.folderName}</span> — {d.nouveaux} nouveau(x), {d.deja_ingeres} déjà ingéré(s){d.erreurs > 0 && <span className="text-destructive">, {d.erreurs} erreur(s)</span>}
            </p>
          ))}
        </div>
      )}

      {/* Liste dossiers */}
      {isLoading ? (
        <div className="flex items-center justify-center h-24">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : dossiers.length === 0 && !driveError ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          <HardDriveUpload className="w-8 h-8 mx-auto mb-2 opacity-30" />
          {!process.env.VITE_DRIVE_CONFIGURED
            ? "Configurez GOOGLE_SA_KEY et DRIVE_CVTHEQUE_FOLDER_ID pour activer le connecteur Drive."
            : "Aucun dossier trouvé dans 21_CVthèque."}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dossier Drive</TableHead>
                <TableHead>Campagne liée</TableHead>
                <TableHead>Dernière synchro</TableHead>
                <TableHead className="text-center">Actif</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {dossiers.map((d) => {
                const m = d.mapping;
                const lastSync = m?.last_sync_at ? new Date(m.last_sync_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : null;
                const recap = m?.last_sync_recap;
                return (
                  <TableRow key={d.driveFolderId} data-testid={`row-drive-${d.driveFolderId}`}>
                    <TableCell className="font-medium text-sm">{d.driveFolderName}</TableCell>
                    <TableCell>
                      {m?.campagne_intitule ? (
                        <div>
                          <p className="text-sm font-medium">{m.campagne_intitule}</p>
                          {m.campagne_filiere && <p className="text-xs text-muted-foreground capitalize">{m.campagne_filiere}</p>}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs italic flex items-center gap-1">
                          <Link2Off className="w-3 h-3" /> Non mappé
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {lastSync ? (
                        <div>
                          <p className="text-xs text-muted-foreground">{lastSync}</p>
                          {recap && (
                            <p className="text-xs text-muted-foreground">
                              +{recap.nouveaux} / {recap.deja_ingeres} skip{recap.erreurs > 0 && <span className="text-destructive"> / {recap.erreurs} err</span>}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {m ? (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                          disabled={toggleMutation.isPending}
                          onClick={() => toggleMutation.mutate({ id: m.id, actif: !m.actif })}
                          data-testid={`btn-toggle-drive-${d.driveFolderId}`}>
                          {m.actif
                            ? <ToggleRight className="w-5 h-5 text-green-600" />
                            : <ToggleLeft className="w-5 h-5 text-muted-foreground" />}
                        </Button>
                      ) : <span className="text-muted-foreground text-xs">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                        onClick={() => setMapperDossier(d)}
                        data-testid={`btn-mapper-${d.driveFolderId}`}>
                        <Link2 className="w-3 h-3" />
                        {m?.campagne_id ? "Re-mapper" : "Mapper"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {mapperDossier && (
        <MapperDialog
          dossier={mapperDossier}
          campagnes={campagnes}
          onClose={() => setMapperDossier(null)}
          onMapped={() => { refetch(); setMapperDossier(null); }}
        />
      )}
    </div>
  );
}

// ─── Sheet principal ───────────────────────────────────────────────────────────

export function RhParametresSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid="rh-parametres-sheet">
        <SheetHeader className="mb-4">
          <SheetTitle>Paramètres RH</SheetTitle>
        </SheetHeader>

        <Tabs defaultValue="campagnes">
          <TabsList className="mb-4">
            <TabsTrigger value="campagnes" data-testid="tab-campagnes">
              <Briefcase className="w-4 h-4 mr-1.5" /> Campagnes
            </TabsTrigger>
            <TabsTrigger value="bureaux" data-testid="tab-bureaux">
              <Building2 className="w-4 h-4 mr-1.5" /> Bureaux
            </TabsTrigger>
            <TabsTrigger value="drive" data-testid="tab-drive">
              <HardDriveUpload className="w-4 h-4 mr-1.5" /> Synchro Drive
            </TabsTrigger>
          </TabsList>

          <TabsContent value="campagnes">
            <CampagnesTab />
          </TabsContent>
          <TabsContent value="bureaux">
            <BureauxTab />
          </TabsContent>
          <TabsContent value="drive">
            <DriveTab />
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
